import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const MONTH_KEYS = [
  'months.jan',
  'months.feb',
  'months.mar',
  'months.apr',
  'months.may',
  'months.jun',
  'months.jul',
  'months.aug',
  'months.sep',
  'months.oct',
  'months.nov',
  'months.dec',
];

interface InvoiceChartProps {
  data: {
    date: string;
    status: string;
  }[];
  year: number;
}

const PAID_STATUS = '2';

const StackedColumnChart: React.FC<InvoiceChartProps> = ({ data, year }) => {
  const { t } = useTranslation();

  const invoicesByMonth = data.reduce(
    (acc, invoice) => {
      const date = new Date(invoice.date);
      if (date.getFullYear() !== year) return acc;

      const status = String(invoice.status) === PAID_STATUS ? 'paid' : 'open';
      acc[date.getMonth()][status] += 1;

      return acc;
    },
    new Array(12).fill(null).map(() => ({ open: 0, paid: 0 })),
  );

  const chartData = {
    labels: MONTH_KEYS.map((key) => t(key)),
    datasets: [
      {
        label: String(t('dashboard.open')),
        data: invoicesByMonth.map((month) => month.open),
        backgroundColor: '#D9EEFA',
      },
      {
        label: String(t('dashboard.paid')),
        data: invoicesByMonth.map((month) => month.paid),
        backgroundColor: '#00C2FF',
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: { precision: 0 },
      },
    },
  };

  return (
    <div className="overflow-x-auto -mx-[4px] px-[4px]">
      <div
        className="min-w-[520px] md:min-w-[600px] h-[220px] md:h-[260px]"
        role="img"
        aria-label={String(t('dashboard.invoicesIssuedInYearLabel', { year }))}
      >
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default StackedColumnChart;
